import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAdminOverview } from "@/admin/api";
import type { AdminOverview } from "@/admin/types";

const cards = [
  {
    key: "adminTotalAgeGroups",
    field: "ageGroups",
    icon: "ri-time-line",
    gradient: "from-[#E8927C] to-[#F4A99A]",
    iconBg: "bg-[#E8927C]",
  },
  {
    key: "adminTotalCategories",
    field: "categories",
    icon: "ri-stack-line",
    gradient: "from-[#2A9D8F] to-[#7CCBB8]",
    iconBg: "bg-[#2A9D8F]",
  },
  {
    key: "adminTotalSkills",
    field: "skills",
    icon: "ri-star-smile-line",
    gradient: "from-[#E9C46A] to-[#F2E0B3]",
    iconBg: "bg-[#E9C46A]",
  },
  {
    key: "adminTotalProducts",
    field: "products",
    icon: "ri-shopping-bag-3-line",
    gradient: "from-[#264653] to-[#577590]",
    iconBg: "bg-[#264653]",
  },
  {
    key: "adminTotalParents",
    field: "parents",
    icon: "ri-parent-line",
    gradient: "from-[#9B5DE5] to-[#C2A0F0]",
    iconBg: "bg-[#9B5DE5]",
  },
  {
    key: "adminTotalChildren",
    field: "children",
    icon: "ri-emotion-happy-line",
    gradient: "from-[#00BBF9] to-[#7DDDF5]",
    iconBg: "bg-[#00BBF9]",
  },
  {
    key: "adminTotalReports",
    field: "reports",
    icon: "ri-file-chart-line",
    gradient: "from-[#F15BB5] to-[#F7A8D3]",
    iconBg: "bg-[#F15BB5]",
  },
] as const;

export default function AdminDashboard() {
  const { t } = useTranslation("common");
  const [overview, setOverview] = useState<AdminOverview | null>(null);

  useEffect(() => {
    let isMounted = true;
    fetchAdminOverview()
      .then((data) => {
        if (!isMounted) return;
        setOverview(data);
      })
      .catch(() => {
        if (!isMounted) return;
        setOverview(null);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy-400">{t("adminOverview")}</h2>
          <p className="text-sm text-gray-500 mt-1">{t("adminOverview")}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {cards.map((card) => {
          const value = overview?.[card.field] ?? 0;
          return (
            <div
              key={card.key}
              className={`relative overflow-hidden rounded-3xl p-5 text-white shadow-[0_25px_60px_-30px_rgba(15,23,42,0.5)] bg-gradient-to-br ${card.gradient} transition-transform hover:-translate-y-1 hover:shadow-[0_30px_70px_-30px_rgba(15,23,42,0.6)]`}
            >
              <div className="absolute -top-8 -end-8 w-32 h-32 rounded-full bg-white/15 blur-2xl pointer-events-none" />
              <div className="absolute -bottom-10 -start-10 w-28 h-28 rounded-full bg-white/10 blur-2xl pointer-events-none" />
              <div className="relative flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-white/85 font-medium mb-2">{t(card.key)}</p>
                  <p className="text-4xl font-extrabold tracking-tight">{value}</p>
                </div>
                <span className="w-12 h-12 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center text-white text-2xl shadow-inner">
                  <i className={card.icon} />
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
